// Etapa 3 — "O meu perfil", Avaliações dos clientes (com aprovação do consultor)
// e criador da imagem de testemunho (A quadrado · B quadrado com foto · C vertical).
// Regra de privacidade: redes e fotografia ficam em perfil_redes / fotos-perfil,
// que só o próprio e a líder/admin conseguem ler (RLS).
import { useState, useEffect, useRef } from "react";
import { CAMPOS_REDES } from "./ModuloConsultores";

const NAVY = "#12244f";
const VERMELHO = "#c8102e";
const DOURADO = "#f5b940";

export const getIniciais = (nome) => {
  const p = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "?";
  return (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
};

// "João Pedro Santos" → "João S."
export const nomeAbreviado = (nome) => {
  const p = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "Cliente";
  return p.length > 1 ? `${p[0]} ${p[p.length - 1][0].toUpperCase()}.` : p[0];
};

// ── Fotografia do perfil (pasta privada) ─────────────────────────────
export function useFotoPerfil(supabase, userId, versao = 0) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let vivo = true, objUrl = null;
    setUrl(null);
    if (!userId) return;
    (async () => {
      const { data: r } = await supabase.from("perfil_redes").select("foto_path").eq("id", userId).maybeSingle();
      if (!r?.foto_path) return;
      const { data: blob } = await supabase.storage.from("fotos-perfil").download(r.foto_path);
      if (!blob || !vivo) return;
      objUrl = URL.createObjectURL(blob);
      setUrl(objUrl);
    })();
    return () => { vivo = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [supabase, userId, versao]);
  return url;
}

export function AvatarPerfil({ foto, nome, size = 36, fundo = NAVY, style = {} }) {
  const base = { width: size, height: size, borderRadius: "50%", flexShrink: 0, ...style };
  if (foto) return <img src={foto} alt={nome || ""} style={{ ...base, objectFit: "cover" }} />;
  return <div className="avatar" style={{ ...base, fontSize: Math.round(size / 3), background: fundo, color: "#fff" }}>{getIniciais(nome)}</div>;
}

// Recorta ao centro em quadrado e reduz (fotos de telemóvel têm vários MB)
const prepararFoto = (file, lado = 512) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const m = Math.min(img.width, img.height);
    const cv = document.createElement("canvas"); cv.width = cv.height = lado;
    cv.getContext("2d").drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, lado, lado);
    URL.revokeObjectURL(img.src);
    cv.toBlob(b => b ? res(b) : rej(new Error("Não foi possível ler a imagem")), "image/jpeg", 0.88);
  };
  img.onerror = () => rej(new Error("Ficheiro de imagem inválido"));
  img.src = URL.createObjectURL(file);
});

// ── O meu perfil ─────────────────────────────────────────────────────
export function MeuPerfil({ supabase, profile, onFotoAlterada }) {
  const uid = profile?.id;
  const [redes, setRedes] = useState(Object.fromEntries(CAMPOS_REDES.map(r => [r.k, ""])));
  const [aGuardar, setAGuardar] = useState(false);
  const [msg, setMsg] = useState("");
  const [versaoFoto, setVersaoFoto] = useState(0);
  const [aCarregar, setACarregar] = useState(false);
  const foto = useFotoPerfil(supabase, uid, versaoFoto);

  useEffect(() => {
    if (!uid) return;
    supabase.from("perfil_redes").select("*").eq("id", uid).maybeSingle().then(({ data }) => {
      setRedes(Object.fromEntries(CAMPOS_REDES.map(r => [r.k, data?.[r.k] || ""])));
    });
  }, [supabase, uid]);

  const guardar = async () => {
    setAGuardar(true); setMsg("");
    const { error } = await supabase.from("perfil_redes").upsert({ id: uid, ...redes, updated_at: new Date().toISOString() });
    setAGuardar(false);
    setMsg(error ? "⚠️ Erro ao guardar: " + error.message : "✅ Redes guardadas.");
  };

  const carregarFoto = async (file) => {
    if (!file) return;
    setACarregar(true); setMsg("");
    try {
      const blob = await prepararFoto(file);
      const path = `${uid}/foto.jpg`;
      const { error: e1 } = await supabase.storage.from("fotos-perfil").upload(path, blob, { upsert: true, contentType: "image/jpeg" });
      if (e1) throw e1;
      const { error: e2 } = await supabase.from("perfil_redes").upsert({ id: uid, foto_path: path, updated_at: new Date().toISOString() });
      if (e2) throw e2;
      setVersaoFoto(v => v + 1); onFotoAlterada && onFotoAlterada();
      setMsg("✅ Fotografia atualizada.");
    } catch (e) { setMsg("⚠️ " + (e.message || "Erro ao carregar a fotografia")); }
    setACarregar(false);
  };

  const removerFoto = async () => {
    if (!window.confirm("Remover a tua fotografia? Voltam a aparecer as iniciais.")) return;
    await supabase.storage.from("fotos-perfil").remove([`${uid}/foto.jpg`]);
    await supabase.from("perfil_redes").upsert({ id: uid, foto_path: null, updated_at: new Date().toISOString() });
    setVersaoFoto(v => v + 1); onFotoAlterada && onFotoAlterada();
    setMsg("Fotografia removida.");
  };

  const cartao = { background: "#fff", borderRadius: 14, border: "1.5px solid #ece9e3", padding: "20px 22px", marginBottom: 16 };
  return (
    <div style={{ maxWidth: 680, padding: "24px 0" }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, marginBottom: 4 }}>O meu perfil</h1>
      <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 20 }}>🔒 Só tu (e a líder) veem a tua fotografia e as tuas redes.</p>

      <div style={cartao}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: 12 }}>Fotografia</div>
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <AvatarPerfil foto={foto} nome={profile?.nome} size={96} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ background: NAVY, color: "#fff", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "center" }}>
              {aCarregar ? "A carregar..." : foto ? "📷 Trocar fotografia" : "📷 Carregar fotografia"}
              <input type="file" accept="image/*" style={{ display: "none" }} disabled={aCarregar} onChange={e => { carregarFoto(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {foto && <button onClick={removerFoto} style={{ background: "#fff", border: "1.5px solid #ece9e3", borderRadius: 8, padding: "7px 14px", fontSize: 12, color: "#6b7280", cursor: "pointer" }}>Remover</button>}
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af", flex: "1 1 200px", lineHeight: 1.5 }}>Aparece nas imagens de testemunho. Sem fotografia aparecem as tuas iniciais ({getIniciais(profile?.nome)}).</div>
        </div>
      </div>

      <div style={cartao}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: 12 }}>🌐 As minhas redes sociais</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {CAMPOS_REDES.map(r => (
            <div key={r.k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span title={r.l} style={{ fontSize: 16, width: 26, textAlign: "center" }}>{r.icon}</span>
              <div style={{ width: 120, fontSize: 12.5, color: "#374151", fontWeight: 600 }}>{r.l}</div>
              <input value={redes[r.k]} onChange={e => setRedes(p => ({ ...p, [r.k]: e.target.value }))} placeholder={r.ph}
                style={{ flex: 1, minWidth: 0, padding: "8px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none" }} />
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
          <button onClick={guardar} disabled={aGuardar} style={{ background: NAVY, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{aGuardar ? "A guardar..." : "Guardar redes"}</button>
          <span style={{ fontSize: 11.5, color: "#9ca3af" }}>O cliente vê-as no fim do formulário de satisfação, para deixar também a avaliação pública.</span>
        </div>
      </div>
      {msg && <div style={{ fontSize: 13, color: msg.startsWith("⚠️") ? "#dc2626" : "#16a34a", fontWeight: 600 }}>{msg}</div>}
    </div>
  );
}

// ── Avaliações ───────────────────────────────────────────────────────
const REDES_PUBLICACAO = ["Instagram", "Facebook", "LinkedIn", "Google", "TikTok", "Idealista"];
const ESTADOS = { por_aprovar: { l: "Por aprovar", cor: "#b45309", bg: "#fffbeb" }, aprovado: { l: "Aprovada", cor: "#16a34a", bg: "#f0fdf4" }, rejeitado: { l: "Rejeitada", cor: "#6b7280", bg: "#f3f4f6" } };

// Frase "Comprou um T3 em Coimbra" a partir do negócio e do papel na referência (…/C)
export const fraseNegocio = (f, negocio, angariacao) => {
  const papel = String(f?.referencia || "").split("/")[3]?.[0] || "";
  const verbo = { C: "Comprou", V: "Vendeu", A: "Arrendou", S: "Arrendou" }[papel] || "Concluiu negócio";
  const tip = negocio?.imovel_tipologia && negocio.imovel_tipologia !== "N/D" ? negocio.imovel_tipologia : "";
  const tipoImovel = String(negocio?.imovel_tipo || angariacao?.tipo_imovel || "").toLowerCase();
  const oque = tip ? `um ${tip}` : tipoImovel ? (/^(moradia|loja|garagem|quinta)/.test(tipoImovel) ? `uma ${tipoImovel}` : `um ${tipoImovel}`) : "";
  const onde = angariacao?.concelho || "";
  if (!oque && !onde) return verbo === "Concluiu negócio" ? "" : verbo + " casa";
  return [verbo, oque, onde ? `em ${onde}` : ""].filter(Boolean).join(" ");
};

const norm = (t) => String(t || "").toLowerCase().replace(/[“”"«»]/g, "").replace(/\s+/g, " ").trim();
// O excerto só pode ter palavras do cliente: cada pedaço (separado por "…") tem de existir no original
export const excertoValido = (excerto, original) => {
  const o = norm(original);
  const partes = String(excerto || "").split(/…|\.\.\./).map(p => norm(p).replace(/^[\s,;:.]+|[\s,;:]+$/g, "")).filter(Boolean);
  return partes.length > 0 && partes.every(p => o.includes(p));
};

export function Avaliacoes({ supabase, formularios, leads, negocios, angariacoes, consultores, profile, userId, isLider, onRefresh }) {
  const [filtro, setFiltro] = useState("por_aprovar");
  const [filtroConsultor, setFiltroConsultor] = useState("");
  const [aprovar, setAprovar] = useState(null); // formulário em aprovação
  const [redesSel, setRedesSel] = useState([]);
  const [imagem, setImagem] = useState(null); // formulário para criar imagem
  const [aviso, setAviso] = useState("");

  const preenchidos = (formularios || []).filter(f => f.preenchido_em);
  const lista = preenchidos
    .filter(f => filtro === "todas" || (f.estado_testemunho || "por_aprovar") === filtro)
    .filter(f => !filtroConsultor || f.consultor_id === filtroConsultor)
    .sort((a, b) => String(b.preenchido_em).localeCompare(String(a.preenchido_em)));
  const contar = (e) => preenchidos.filter(f => (f.estado_testemunho || "por_aprovar") === e).length;
  const leadDe = (f) => (leads || []).find(l => String(l.id) === String(f.lead_id));
  const negocioDe = (f) => (negocios || []).find(n => n.id === f.negocio_id);
  const angDe = (n) => (angariacoes || []).find(a => a.id === n?.angariacao_id);
  const eMeu = (f) => f.consultor_id === userId;

  const mostrarAviso = (t) => { setAviso(t); setTimeout(() => setAviso(""), 3500); };

  const textoParaCopiar = (f) => {
    const n = negocioDe(f);
    const frase = fraseNegocio(f, n, angDe(n));
    const estrelas = "⭐".repeat(Math.max(0, Math.min(5, f.avaliacao_geral || 0)));
    return `${estrelas}\n“${(f.excerto_testemunho || f.testemunho_publico || "").trim()}”\n— ${nomeAbreviado(leadDe(f)?.nome)}${frase ? " · " + frase : ""}`;
  };

  const confirmarAprovacao = async () => {
    const f = aprovar;
    const { error } = await supabase.from("formularios_satisfacao").update({
      estado_testemunho: "aprovado", aprovado_em: new Date().toISOString(), aprovado_por: userId, publicado_em: redesSel,
    }).eq("id", f.id);
    if (error) { mostrarAviso("⚠️ " + error.message); return; }
    try { await navigator.clipboard.writeText(textoParaCopiar(f)); mostrarAviso("✅ Aprovada. Texto copiado, pronto a colar."); }
    catch { mostrarAviso("✅ Aprovada."); }
    setAprovar(null); onRefresh && onRefresh();
  };

  const mudarEstado = async (f, estado) => {
    const { error } = await supabase.from("formularios_satisfacao").update({ estado_testemunho: estado, ...(estado === "por_aprovar" ? { aprovado_em: null, aprovado_por: null } : {}) }).eq("id", f.id);
    if (error) mostrarAviso("⚠️ " + error.message); else onRefresh && onRefresh();
  };

  const copiar = async (f) => {
    try { await navigator.clipboard.writeText(textoParaCopiar(f)); mostrarAviso("📋 Texto copiado."); } catch { mostrarAviso("Não foi possível copiar."); }
  };

  const btn = (bg, cor, borda) => ({ background: bg, color: cor, border: borda ? `1.5px solid ${borda}` : "none", borderRadius: 8, padding: "7px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer" });

  return (
    <div style={{ padding: "24px 0" }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, marginBottom: 4 }}>⭐ Avaliações</h1>
      <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 18 }}>Os testemunhos ficam só na app até o consultor os aprovar. A imagem só pode ser criada se o cliente autorizou a publicação.</p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        {[["por_aprovar", `Por aprovar (${contar("por_aprovar")})`], ["aprovado", `Aprovadas (${contar("aprovado")})`], ["rejeitado", `Rejeitadas (${contar("rejeitado")})`], ["todas", `Todas (${preenchidos.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setFiltro(k)} style={{ ...btn(filtro === k ? NAVY : "#fff", filtro === k ? "#fff" : "#374151", filtro === k ? null : "#ece9e3"), fontWeight: 600 }}>{l}</button>
        ))}
        {isLider && (
          <select value={filtroConsultor} onChange={e => setFiltroConsultor(e.target.value)} style={{ padding: "7px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 12, background: "#fff" }}>
            <option value="">Todos os consultores</option>
            {(consultores || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        )}
      </div>
      {aviso && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{aviso}</div>}

      {lista.length === 0 && <div style={{ background: "#fff", border: "1.5px dashed #ece9e3", borderRadius: 12, padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>Sem avaliações nesta lista.</div>}

      <div style={{ display: "grid", gap: 12 }}>
        {lista.map(f => {
          const est = ESTADOS[f.estado_testemunho || "por_aprovar"];
          const n = negocioDe(f);
          const frase = fraseNegocio(f, n, angDe(n));
          const temTexto = !!(f.testemunho_publico || "").trim();
          const podeImagem = temTexto && f.autoriza_testemunho && f.estado_testemunho === "aprovado";
          return (
            <div key={f.id} style={{ background: "#fff", border: "1.5px solid #ece9e3", borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{leadDe(f)?.nome || "Cliente"} <span style={{ color: DOURADO, marginLeft: 6 }}>{"★".repeat(f.avaliacao_geral || 0)}<span style={{ color: "#e5e7eb" }}>{"★".repeat(Math.max(0, 5 - (f.avaliacao_geral || 0)))}</span></span></div>
                  <div style={{ fontSize: 11.5, color: "#9ca3af" }}>Ref. {f.referencia || "—"}{frase ? ` · ${frase}` : ""}{isLider ? ` · ${f.consultor_nome || ""}` : ""}</div>
                </div>
                <span style={{ alignSelf: "flex-start", background: est.bg, color: est.cor, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{est.l}</span>
              </div>
              {temTexto
                ? <div style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 14, color: "#374151", lineHeight: 1.55, marginBottom: 8 }}>“{f.testemunho_publico}”</div>
                : <div style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 8 }}>O cliente não escreveu testemunho (só a classificação).</div>}
              <div style={{ fontSize: 11.5, color: f.autoriza_testemunho ? "#16a34a" : "#dc2626", marginBottom: 10 }}>
                {f.autoriza_testemunho ? "✓ Cliente autorizou a publicação" : "✕ Cliente NÃO autorizou a publicação"}
                {(f.publicado_em || []).length > 0 && <span style={{ color: "#6b7280" }}> · Publicar em: {(f.publicado_em || []).join(", ")}</span>}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {eMeu(f) && temTexto && f.autoriza_testemunho && (f.estado_testemunho || "por_aprovar") !== "aprovado" && (
                  <button onClick={() => { setAprovar(f); setRedesSel(f.publicado_em || ["Instagram", "Facebook"]); }} style={btn("#16a34a", "#fff")}>✓ Aprovar</button>
                )}
                {eMeu(f) && (f.estado_testemunho || "por_aprovar") === "por_aprovar" && (
                  <button onClick={() => mudarEstado(f, "rejeitado")} style={btn("#fff", "#6b7280", "#ece9e3")}>Não publicar</button>
                )}
                {eMeu(f) && (f.estado_testemunho || "por_aprovar") !== "por_aprovar" && (
                  <button onClick={() => mudarEstado(f, "por_aprovar")} style={btn("#fff", "#6b7280", "#ece9e3")}>↺ Voltar a "Por aprovar"</button>
                )}
                {f.estado_testemunho === "aprovado" && temTexto && <button onClick={() => copiar(f)} style={btn("#fff", NAVY, "#c7d2fe")}>📋 Copiar texto</button>}
                {podeImagem && <button onClick={() => setImagem(f)} style={btn(NAVY, "#fff")}>🖼 Criar imagem</button>}
                {!eMeu(f) && (f.estado_testemunho || "por_aprovar") === "por_aprovar" && <span style={{ fontSize: 11.5, color: "#9ca3af", alignSelf: "center" }}>À espera da aprovação do consultor</span>}
              </div>
            </div>
          );
        })}
      </div>

      {aprovar && (
        <div onClick={() => setAprovar(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, maxWidth: 440, width: "100%" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Aprovar testemunho</div>
            <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 14 }}>Onde o vais publicar? (podes mudar depois)</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {REDES_PUBLICACAO.map(r => {
                const on = redesSel.includes(r);
                return <button key={r} onClick={() => setRedesSel(p => on ? p.filter(x => x !== r) : [...p, r])} style={{ ...btn(on ? NAVY : "#fff", on ? "#fff" : "#374151", on ? null : "#ece9e3"), fontWeight: 600 }}>{on ? "✓ " : ""}{r}</button>;
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setAprovar(null)} style={btn("#fff", "#6b7280", "#ece9e3")}>Cancelar</button>
              <button onClick={confirmarAprovacao} style={btn("#16a34a", "#fff")}>✓ Aprovar e copiar texto</button>
            </div>
          </div>
        </div>
      )}

      {imagem && (() => {
        const n = negocioDe(imagem);
        return <GeradorImagemTestemunho supabase={supabase} formulario={imagem} cliente={nomeAbreviado(leadDe(imagem)?.nome)} frase={fraseNegocio(imagem, n, angDe(n))}
          onFechar={() => setImagem(null)} onExcertoGuardado={onRefresh} />;
      })()}
    </div>
  );
}

// ── Imagem de testemunho (canvas) ────────────────────────────────────
const carregarImagem = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

const partirLinhas = (ctx, texto, larguraMax) => {
  const palavras = texto.split(/\s+/).filter(Boolean); const linhas = []; let linha = "";
  for (const p of palavras) {
    const teste = linha ? linha + " " + p : p;
    if (ctx.measureText(teste).width > larguraMax && linha) { linhas.push(linha); linha = p; } else linha = teste;
  }
  if (linha) linhas.push(linha);
  return linhas;
};

const desenharEstrela = (ctx, cx, cy, r, cor) => {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const raio = i % 2 === 0 ? r : r * 0.45, ang = -Math.PI / 2 + i * Math.PI / 5;
    ctx[i === 0 ? "moveTo" : "lineTo"](cx + raio * Math.cos(ang), cy + raio * Math.sin(ang));
  }
  ctx.closePath(); ctx.fillStyle = cor; ctx.fill();
};

// Desenha a versão pedida num canvas. Exportada para os testes.
export async function desenharTestemunho(cv, { versao, texto, estrelas = 5, cliente, frase, consultorNome, cargo, fotoConsultor, fotoImovel, logos }) {
  const W = 1080, H = versao === "C" ? 1920 : 1080, vertical = versao === "C";
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  const M = 96; // margem lateral

  // Fundo azul-marinho
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, "#0e1d45"); g.addColorStop(1, "#1d3a78");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  if (versao === "B" && fotoImovel) {
    // foto do imóvel a encher o quadrado + filtro azulado para leitura fácil
    const esc = Math.max(W / fotoImovel.width, H / fotoImovel.height);
    const w = fotoImovel.width * esc, h = fotoImovel.height * esc;
    ctx.drawImage(fotoImovel, (W - w) / 2, (H - h) / 2, w, h);
    ctx.fillStyle = "rgba(14, 34, 88, 0.62)"; ctx.fillRect(0, 0, W, H);
    const v = ctx.createLinearGradient(0, 0, 0, H);
    v.addColorStop(0, "rgba(10, 22, 58, 0.35)"); v.addColorStop(0.45, "rgba(10, 22, 58, 0.55)"); v.addColorStop(1, "rgba(10, 22, 58, 0.92)");
    ctx.fillStyle = v; ctx.fillRect(0, 0, W, H);
  }

  // Logótipos: Equipa Sónia Cunha (dourado) à esquerda · RE/MAX White (branco) à direita
  const topo = vertical ? 150 : 70;
  const hLogo = vertical ? 118 : 104;
  if (logos?.equipa) { const w = logos.equipa.width * hLogo / logos.equipa.height; ctx.drawImage(logos.equipa, M - 6, topo, w, hLogo); }
  if (logos?.remax) { const h2 = hLogo * 0.9, w = logos.remax.width * h2 / logos.remax.height; ctx.drawImage(logos.remax, W - M - w, topo + (hLogo - h2) / 2, w, h2); }

  // Rodapé (consultor) — calculado primeiro para saber o espaço do texto
  const rAv = vertical ? 70 : 56;
  const yAv = vertical ? H - 290 : H - 146;
  const yDiv = yAv - rAv - (vertical ? 46 : 36);

  // Estrelas
  let y = topo + hLogo + (vertical ? 120 : 76);
  const rE = vertical ? 40 : 34;
  for (let i = 0; i < 5; i++) desenharEstrela(ctx, M + rE + i * rE * 2.2, y + rE, rE, i < estrelas ? DOURADO : "rgba(255,255,255,0.22)");
  y += rE * 2 + (vertical ? 60 : 40);

  // Aspas vermelhas + testemunho (letra elegante), tamanho ajustado ao espaço
  ctx.fillStyle = VERMELHO; ctx.font = `italic 700 ${vertical ? 190 : 160}px "Playfair Display", Georgia, serif`;
  ctx.textBaseline = "top";
  ctx.fillText("“", M - 8, y - (vertical ? 20 : 18));
  y += vertical ? 120 : 96;

  const linhaCliente = vertical ? 44 : 34;
  const espaco = yDiv - y - linhaCliente - (vertical ? 70 : 46);
  let tam = vertical ? 78 : 60, linhas = [], alt = 0;
  for (; tam >= 28; tam -= 2) {
    ctx.font = `italic 600 ${tam}px "Playfair Display", Georgia, serif`;
    linhas = partirLinhas(ctx, texto + "”", W - 2 * M);
    alt = linhas.length * tam * 1.22;
    if (alt <= espaco) break;
  }
  ctx.fillStyle = "#ffffff";
  linhas.forEach((l, i) => ctx.fillText(l, M, y + i * tam * 1.22));
  y += alt + (vertical ? 44 : 30);

  // Cliente · frase
  ctx.font = `700 ${vertical ? 38 : 30}px "DM Sans", Arial, sans-serif`; ctx.fillStyle = "#e3e8f4";
  ctx.fillText(cliente, M, y);
  if (frase) {
    const wC = ctx.measureText(cliente).width;
    ctx.font = `400 ${vertical ? 38 : 30}px "DM Sans", Arial, sans-serif`;
    ctx.fillText(" · " + frase, M + wC, y);
  }

  // Linha divisória
  ctx.fillStyle = "rgba(255,255,255,0.2)"; ctx.fillRect(M, yDiv, W - 2 * M, 2);

  // Consultor: fotografia (ou iniciais) + nome + cargo
  const cx = M + rAv, cy = yAv;
  ctx.beginPath(); ctx.arc(cx, cy, rAv + 6, 0, Math.PI * 2); ctx.fillStyle = "#ffffff"; ctx.fill();
  ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, rAv, 0, Math.PI * 2); ctx.clip();
  if (fotoConsultor) ctx.drawImage(fotoConsultor, cx - rAv, cy - rAv, rAv * 2, rAv * 2);
  else {
    ctx.fillStyle = "#e8edf5"; ctx.fillRect(cx - rAv, cy - rAv, rAv * 2, rAv * 2);
    ctx.fillStyle = NAVY; ctx.font = `700 ${Math.round(rAv * 0.72)}px "DM Sans", Arial, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(getIniciais(consultorNome), cx, cy + 2); ctx.textAlign = "left";
  }
  ctx.restore();
  ctx.textBaseline = "alphabetic";
  const xN = cx + rAv + 30;
  ctx.fillStyle = "#ffffff"; ctx.font = `700 ${vertical ? 46 : 38}px "DM Sans", Arial, sans-serif`;
  ctx.fillText(consultorNome || "", xN, cy - 4);
  ctx.fillStyle = "#c9d3e6"; ctx.font = `400 ${vertical ? 32 : 26}px "DM Sans", Arial, sans-serif`;
  ctx.fillText(cargo || "", xN, cy + (vertical ? 44 : 36));
}

const CARGOS = ["Consultor · RE/MAX White", "Consultora · RE/MAX White", "Team Leader · RE/MAX White"];

function GeradorImagemTestemunho({ supabase, formulario, cliente, frase: fraseInicial, onFechar, onExcertoGuardado }) {
  const original = (formulario.testemunho_publico || "").trim();
  const [versao, setVersao] = useState("A");
  const [excerto, setExcerto] = useState((formulario.excerto_testemunho || original).trim());
  const [frase, setFrase] = useState(fraseInicial || "");
  const [cargo, setCargo] = useState(() => { try { return localStorage.getItem("testemunho_cargo") || CARGOS[0]; } catch { return CARGOS[0]; } });
  const [fotoImovel, setFotoImovel] = useState(null);
  const [recursos, setRecursos] = useState(null);
  const [erro, setErro] = useState("");
  const cvRef = useRef(null);
  const fotoConsultorUrl = useFotoPerfil(supabase, formulario.consultor_id);

  const valido = excertoValido(excerto, original);

  // Letras e logótipos têm de estar carregados antes de desenhar
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        await Promise.all([
          document.fonts.load('italic 600 60px "Playfair Display"'), document.fonts.load('italic 700 60px "Playfair Display"'),
          document.fonts.load('400 30px "DM Sans"'), document.fonts.load('700 30px "DM Sans"'),
        ]);
      } catch { /* usa letra de recurso */ }
      const [equipa, remax] = await Promise.all([carregarImagem("/marca/equipa-sc-dourado.png"), carregarImagem("/marca/remax-white-branco.png")]);
      if (vivo) setRecursos(r => ({ ...r, logos: { equipa, remax } }));
    })().catch(() => setErro("Não foi possível carregar os logótipos."));
    return () => { vivo = false; };
  }, []);
  useEffect(() => {
    if (!fotoConsultorUrl) return;
    carregarImagem(fotoConsultorUrl).then(img => setRecursos(r => ({ ...r, foto: img }))).catch(() => {});
  }, [fotoConsultorUrl]);

  useEffect(() => {
    if (!recursos?.logos || !cvRef.current || !valido) return;
    if (versao === "B" && !fotoImovel) return;
    desenharTestemunho(cvRef.current, {
      versao, texto: excerto.trim(), estrelas: formulario.avaliacao_geral || 5, cliente, frase, consultorNome: formulario.consultor_nome,
      cargo, fotoConsultor: recursos.foto, fotoImovel, logos: recursos.logos,
    });
  }, [recursos, versao, excerto, frase, cargo, fotoImovel, valido, cliente, formulario]);

  const escolherFotoImovel = (file) => {
    if (!file) return;
    carregarImagem(URL.createObjectURL(file)).then(setFotoImovel).catch(() => setErro("Ficheiro de imagem inválido."));
  };

  const nomeFicheiro = `testemunho_${String(formulario.referencia || formulario.id).replace(/[^A-Za-z0-9-]/g, "_")}_${versao === "C" ? "vertical" : "quadrado" + (versao === "B" ? "_foto" : "")}.png`;
  const obterBlob = () => new Promise(res => cvRef.current.toBlob(res, "image/png"));
  const guardarExcerto = async () => {
    const valor = excerto.trim() === original ? null : excerto.trim();
    if (valor !== (formulario.excerto_testemunho || null)) {
      await supabase.from("formularios_satisfacao").update({ excerto_testemunho: valor }).eq("id", formulario.id);
      onExcertoGuardado && onExcertoGuardado();
    }
  };
  const descarregar = async () => {
    await guardarExcerto();
    const blob = await obterBlob(); const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = nomeFicheiro; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const partilhar = async () => {
    await guardarExcerto();
    const blob = await obterBlob(); const file = new File([blob], nomeFicheiro, { type: "image/png" });
    try { await navigator.share({ files: [file] }); } catch { /* cancelado */ }
  };
  const podePartilhar = typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [new File([""], "x.png", { type: "image/png" })] });

  const pronto = valido && recursos?.logos && (versao !== "B" || fotoImovel);
  const opt = (on) => ({ background: on ? NAVY : "#fff", color: on ? "#fff" : "#374151", border: on ? "none" : "1.5px solid #ece9e3", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" });
  const lbl = { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", display: "block", marginBottom: 5 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, overflowY: "auto", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, maxWidth: 980, margin: "20px auto", padding: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>🖼 Imagem de testemunho <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>Ref. {formulario.referencia}</span></div>
          <button onClick={onFechar} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#6b7280" }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <span style={lbl}>Versão</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button onClick={() => setVersao("A")} style={opt(versao === "A")}>A · Quadrado</button>
                <button onClick={() => setVersao("B")} style={opt(versao === "B")}>B · Quadrado com foto</button>
                <button onClick={() => setVersao("C")} style={opt(versao === "C")}>C · Vertical (Stories/TikTok)</button>
              </div>
            </div>
            {versao === "B" && (
              <div>
                <span style={lbl}>Fotografia do imóvel</span>
                <label style={{ ...opt(false), display: "inline-block" }}>
                  📷 {fotoImovel ? "Trocar fotografia" : "Escolher fotografia"}
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { escolherFotoImovel(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>A foto não fica guardada. Leva um filtro azul para o texto se ler bem.</div>
              </div>
            )}
            <div>
              <span style={lbl}>Testemunho (podes encurtar)</span>
              <textarea value={excerto} onChange={e => setExcerto(e.target.value)} rows={5} style={{ width: "100%", padding: "9px 11px", border: `1.5px solid ${valido ? "#ece9e3" : "#fca5a5"}`, borderRadius: 8, fontSize: 13, fontFamily: "Georgia, serif", fontStyle: "italic", resize: "vertical" }} />
              <div style={{ fontSize: 11, color: valido ? "#9ca3af" : "#dc2626", marginTop: 3, lineHeight: 1.45 }}>
                {valido ? "Podes apagar partes, mas não mudar as palavras do cliente. Usa … onde cortares no meio." : "⚠️ O texto tem palavras que o cliente não escreveu. Só podes apagar partes do original."}
              </div>
              {excerto.trim() !== original && <button onClick={() => setExcerto(original)} style={{ background: "none", border: "none", color: NAVY, fontSize: 11.5, cursor: "pointer", padding: 0, marginTop: 4, textDecoration: "underline" }}>Repor texto completo</button>}
            </div>
            <div>
              <span style={lbl}>Linha do cliente</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{cliente} ·</span>
                <input value={frase} onChange={e => setFrase(e.target.value)} placeholder="Comprou um T3 em Coimbra" style={{ flex: 1, minWidth: 0, padding: "7px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 13 }} />
              </div>
            </div>
            <div>
              <span style={lbl}>Cargo por baixo do nome</span>
              <select value={cargo} onChange={e => { setCargo(e.target.value); try { localStorage.setItem("testemunho_cargo", e.target.value); } catch { /* sem armazenamento */ } }} style={{ padding: "7px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 13, background: "#fff" }}>
                {CARGOS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              <button onClick={descarregar} disabled={!pronto} style={{ background: pronto ? NAVY : "#9ca3af", color: "#fff", border: "none", borderRadius: 9, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: pronto ? "pointer" : "default" }}>⬇ Descarregar PNG</button>
              {podePartilhar && <button onClick={partilhar} disabled={!pronto} style={{ background: "#fff", color: NAVY, border: `1.5px solid ${NAVY}`, borderRadius: 9, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>📤 Partilhar</button>}
            </div>
            {erro && <div style={{ fontSize: 12, color: "#dc2626" }}>{erro}</div>}
          </div>
          <div style={{ flex: "1 1 360px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
            {versao === "B" && !fotoImovel
              ? <div style={{ width: "100%", maxWidth: 420, aspectRatio: "1", background: "#f3f4f6", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13, textAlign: "center", padding: 20 }}>Escolhe a fotografia do imóvel para ver a pré-visualização.</div>
              : <canvas ref={cvRef} style={{ width: "100%", maxWidth: versao === "C" ? 300 : 440, borderRadius: 10, boxShadow: "0 6px 24px rgba(0,0,0,0.18)", display: valido ? "block" : "none" }} />}
          </div>
        </div>
      </div>
    </div>
  );
}
